import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Avatar,
  IconButton,
  InputAdornment,
  TextField,
  Chip,
  Divider,
  Badge,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Message as MessageIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Campaign as CampaignIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Storefront,
  Person,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import pb from './pocketbase';

const SellerChat = () => {
  const theme = useTheme();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // State management
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedSeller, setSelectedSeller] = useState('');
  const [sellers, setSellers] = useState([]);
  
  // Selected conversation state
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Fetch sellers for the dropdown
  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const result = await pb.collection('sellers').getList(1, 50, {
          sort: 'id',
          fields: 'id,email,shop_name' // Added shop_name field
        });
        setSellers(result.items);
      } catch (err) {
        console.error('Error fetching sellers:', err);
      }
    };
    fetchSellers();
  }, []);

  // Fetch messages for selected conversation
  useEffect(() => {
    const fetchMessages = async () => {
      if (!selectedConversation) return;
      
      setLoadingMessages(true);
      try {
        const result = await pb.collection('seller_chat').getList(1, 100, {
          filter: `user = '${selectedConversation.userId}' && seller = '${selectedConversation.sellerId}'`,
          sort: 'created',
          expand: 'user,seller',
        });
        
        setSelectedMessages(result.items);
        
        // Mark unread messages as read
        const unreadMessages = result.items.filter(msg => 
          msg.sender === 'user' && !msg.read_status
        );
        
        if (unreadMessages.length > 0) {
          await Promise.all(unreadMessages.map(msg => 
            pb.collection('seller_chat').update(msg.id, { read_status: true })
          ));
        }
      } catch (err) {
        console.error('Error fetching messages:', err);
      } finally {
        setLoadingMessages(false);
      }
    };

    fetchMessages();

    // Set up real-time subscription
    if (selectedConversation) {
      pb.collection('seller_chat').subscribe('*', function(e) {
        if (e.record && 
            e.record.user === selectedConversation.userId && 
            e.record.seller === selectedConversation.sellerId) {
          if (e.action === 'create') {
            setSelectedMessages(prev => [...prev, e.record]);
            if (e.record.sender === 'user' && !e.record.read_status) {
              pb.collection('seller_chat').update(e.record.id, { read_status: true });
            }
          }
        }
      });

      return () => pb.collection('seller_chat').unsubscribe();
    }
  }, [selectedConversation]);

  // Fetch conversations
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true);
        
        let filter = '';
        
        if (selectedSeller) {
          filter = `seller = '${selectedSeller}'`;
        }
        
        if (selectedFilter === 'unread') {
          const unreadFilter = "sender = 'user' && read_status = false";
          filter = filter ? `${filter} && ${unreadFilter}` : unreadFilter;
        }
        
        if (searchTerm) {
          // Change the search filter to only search the message content
          const searchFilter = `message ~ '${searchTerm}'`;
          filter = filter ? `${filter} && (${searchFilter})` : searchFilter;
        }
        
        const result = await pb.collection('seller_chat').getList(1, 500, {
          filter,
          sort: '-created',
          expand: 'user,seller',
        });
        
        // Client-side filtering for user/seller name/email when searchTerm exists
        let filteredItems = result.items;
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          filteredItems = result.items.filter(message => {
            const userMatch = message.expand?.user?.name?.toLowerCase().includes(searchLower) ||
                            message.expand?.user?.email?.toLowerCase().includes(searchLower);
            const sellerMatch = message.expand?.seller?.name?.toLowerCase().includes(searchLower) ||
                              message.expand?.seller?.email?.toLowerCase().includes(searchLower);
            return userMatch || sellerMatch || message.message?.toLowerCase().includes(searchLower);
          });
        }
        
        // Group messages by user-seller pair
        const conversationsMap = {};
        
        filteredItems.forEach(message => {
          const key = `${message.user}-${message.seller}`;
          if (!conversationsMap[key]) {
            conversationsMap[key] = {
              userId: message.user,
              sellerId: message.seller,
              messages: [],
              lastMessage: null,
              created: null,
              unreadCount: 0,
              userName: message.expand?.user?.name || 'Unknown User',
              userEmail: message.expand?.user?.email || '',
              sellerName: message.expand?.seller?.shop_name || 'Unknown Seller',
              sellerEmail: message.expand?.seller?.email || '',
            };
          }
          
          conversationsMap[key].messages.push(message);
          
          const messageDate = new Date(message.created).getTime();
          const currentLastMessageDate = conversationsMap[key].created ? 
            new Date(conversationsMap[key].created).getTime() : 0;
            
          if (!conversationsMap[key].lastMessage || messageDate > currentLastMessageDate) {
            conversationsMap[key].lastMessage = message.message || '(Attachment)';
            conversationsMap[key].created = message.created;
            conversationsMap[key].hasAttachment = message.attachment && message.attachment.length > 0;
          }
          
          if (message.sender === 'user' && !message.read_status) {
            conversationsMap[key].unreadCount++;
          }
        });
        
        const conversationsArray = Object.values(conversationsMap)
          .sort((a, b) => new Date(b.created) - new Date(a.created));
        
        setConversations(conversationsArray);
        setError(null);
      } catch (err) {
        console.error('Error fetching conversations:', err);
        setError('Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };
    
    fetchConversations();
  }, [searchTerm, selectedFilter, selectedSeller]);

  // Handle sending a new message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!newMessage.trim() && selectedFiles.length === 0) return;
    if (!selectedConversation) return;

    try {
      setSending(true);
      
      const messageData = {
        message: newMessage.trim(),
        user: selectedConversation.userId,
        seller: selectedConversation.sellerId,
        sender: 'seller',
        read_status: false
      };

      if (selectedFiles.length > 0) {
        const formData = new FormData();
        selectedFiles.forEach(file => {
          formData.append('attachment', file);
        });
        Object.keys(messageData).forEach(key => {
          formData.append(key, messageData[key]);
        });
        await pb.collection('seller_chat').create(formData);
      } else {
        await pb.collection('seller_chat').create(messageData);
      }

      setNewMessage('');
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(prevFiles => [...prevFiles, ...files]);
    event.target.value = '';
  };

  // Remove a selected file
  const handleRemoveFile = (index) => {
    setSelectedFiles(prevFiles => 
      prevFiles.filter((_, i) => i !== index)
    );
  };

  // Format message date
  const formatMessageDate = (dateString) => {
    const messageDate = new Date(dateString);
    const now = new Date();
    
    if (messageDate.toDateString() === now.toDateString()) {
      return format(messageDate, 'h:mm a');
    }
    
    if (now - messageDate < 7 * 24 * 60 * 60 * 1000) {
      return format(messageDate, 'EEEE h:mm a');
    }
    
    return format(messageDate, 'MMM d, yyyy h:mm a');
  };

  return (
    <Box sx={{ 
      p: 3, 
      height: 'calc(100vh - 80px)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Seller Chat
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and manage conversations between sellers and customers
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        gap: 2,
        flexGrow: 1,
        minHeight: 0
      }}>
        {/* Conversations List */}
        <Box sx={{ 
          width: { xs: '100%', md: 320 },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          height: { xs: selectedConversation ? 0 : '100%', md: '100%' },
          overflow: 'hidden',
          transition: 'height 0.3s ease-in-out'
        }}>
          <Paper sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Search and Filters */}
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Select Seller</InputLabel>
                <Select
                  value={selectedSeller}
                  onChange={(e) => setSelectedSeller(e.target.value)}
                  label="Select Seller"
                >
                  <MenuItem value="">All Sellers</MenuItem>
                  {sellers.map(seller => (
                    <MenuItem key={seller.id} value={seller.id}>
                      {seller.shop_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setSelectedFilter(prev => prev === 'all' ? 'unread' : 'all')}
                size="medium"
                fullWidth
              >
                {selectedFilter === 'all' ? 'All Messages' : 'Unread Messages'}
              </Button>
            </Box>
            
            <Divider />
            
            {/* Conversations List */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box sx={{ p: 3 }}>
                  <Alert severity="error">{error}</Alert>
                </Box>
              ) : conversations.length === 0 ? (
                <Box sx={{ 
                  py: 10, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <MessageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No conversations found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400, mb: 2 }}>
                    {searchTerm || selectedFilter !== 'all' 
                      ? 'Try adjusting your search or filters' 
                      : 'When customers message sellers, their conversations will appear here'}
                  </Typography>
                </Box>
              ) : (
                conversations.map(conversation => (
                  <Card 
                    key={`${conversation.userId}-${conversation.sellerId}`}
                    elevation={0}
                    onClick={() => setSelectedConversation(conversation)}
                    sx={{ 
                      mb: 1, 
                      mx: 1,
                      cursor: 'pointer',
                      borderRadius: 2,
                      bgcolor: selectedConversation?.userId === conversation.userId && 
                              selectedConversation?.sellerId === conversation.sellerId
                        ? theme.palette.primary.light
                        : conversation.unreadCount > 0 
                        ? theme.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(66, 165, 245, 0.08)'
                        : 'background.paper',
                      '&:hover': {
                        bgcolor: selectedConversation?.userId === conversation.userId &&
                                selectedConversation?.sellerId === conversation.sellerId
                          ? theme.palette.primary.light
                          : theme.palette.action.hover
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          variant="dot"
                          color={conversation.unreadCount > 0 ? "primary" : "default"}
                        >
                          <Box sx={{ display: 'flex' }}>
                            <Avatar 
                              sx={{ 
                                bgcolor: theme.palette.primary.main,
                                width: 32,
                                height: 32
                              }}
                            >
                              <Person />
                            </Avatar>
                            <Avatar 
                              sx={{ 
                                ml: -1.5,
                                bgcolor: theme.palette.secondary.main,
                                width: 32,
                                height: 32
                              }}
                            >
                              <Storefront />
                            </Avatar>
                          </Box>
                        </Badge>
                        
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600} noWrap>
                              {conversation.userName || 'User'} • {conversation.sellerName || 'Seller'}
                            </Typography>
                            {conversation.unreadCount > 0 && (
                              <Chip 
                                label={conversation.unreadCount} 
                                color="primary" 
                                size="small"
                                sx={{ height: 20, fontSize: '0.75rem' }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {formatDistanceToNow(new Date(conversation.created), { addSuffix: true })}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ 
                              mt: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {conversation.lastMessage}
                            {conversation.hasAttachment && (
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
                                <AttachFileIcon fontSize="small" sx={{ fontSize: 14 }} />
                              </Box>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          </Paper>
        </Box>

        {/* Messages View */}
        <Box sx={{ 
          flexGrow: 1,
          height: { xs: selectedConversation ? '100%' : 0, md: '100%' },
          overflow: 'hidden',
          transition: 'height 0.3s ease-in-out'
        }}>
          <Paper sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            position: 'relative'
          }}>
            {selectedConversation && (
              <Box sx={{ 
                position: 'absolute',
                top: theme.spacing(1),
                left: theme.spacing(1),
                display: { xs: 'block', md: 'none' },
                zIndex: 3,
              }}>
                <IconButton
                  onClick={() => setSelectedConversation(null)}
                  sx={{ 
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    '&:hover': { bgcolor: 'background.paper' }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Box>
            )}
            
            {selectedConversation ? (
              <>
                {/* Conversation Header */}
                <Box sx={{ 
                  p: 2, 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Box sx={{ display: 'flex' }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                      <Person />
                    </Avatar>
                    <Avatar sx={{ ml: -1, bgcolor: theme.palette.secondary.main }}>
                      <Storefront />
                    </Avatar>
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {selectedConversation.userName || 'User'} • {selectedConversation.sellerName || 'Seller'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedConversation.userEmail} • {selectedConversation.sellerEmail}
                    </Typography>
                  </Box>
                </Box>

                {/* Messages Container */}
                <Box sx={{ 
                  flexGrow: 1, 
                  overflow: 'auto',
                  p: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)'
                }}>
                  {loadingMessages ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <CircularProgress />
                    </Box>
                  ) : selectedMessages.length === 0 ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      justifyContent: 'center', 
                      height: '100%' 
                    }}>
                      <CampaignIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No messages yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
                        The conversation history will appear here
                      </Typography>
                    </Box>
                  ) : (
                    selectedMessages.map((message) => {
                      const isFromUser = message.sender === 'user';
                      const hasAttachments = message.attachment && message.attachment.length > 0;
                      
                      return (
                        <Box
                          key={message.id}
                          sx={{
                            display: 'flex',
                            justifyContent: isFromUser ? 'flex-start' : 'flex-end',
                            mb: 2,
                          }}
                        >
                          {isFromUser && (
                            <Avatar 
                              sx={{ 
                                width: 32, 
                                height: 32, 
                                mr: 1,
                                mt: 'auto',
                                bgcolor: theme.palette.primary.main
                              }}
                            >
                              <Person />
                            </Avatar>
                          )}
                          
                          <Box sx={{ maxWidth: '70%' }}>
                            <Card
                              sx={{
                                bgcolor: isFromUser 
                                  ? theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'
                                  : theme.palette.secondary.main,
                                color: isFromUser 
                                  ? 'text.primary' 
                                  : theme.palette.secondary.contrastText,
                                borderRadius: isFromUser ? '0 8px 8px 8px' : '8px 0 8px 8px'
                              }}
                            >
                              <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                                {message.message && (
                                  <Typography variant="body2">
                                    {message.message}
                                  </Typography>
                                )}
                                {hasAttachments && (
                                  <Box sx={{ mt: message.message ? 1 : 0 }}>
                                    {message.attachment.map((file, idx) => (
                                      <Box
                                        key={idx}
                                        component="img"
                                        src={pb.files.getUrl(message, file)}
                                        alt={`Attachment ${idx + 1}`}
                                        sx={{
                                          maxWidth: '100%',
                                          maxHeight: 200,
                                          borderRadius: 1,
                                          mt: 1
                                        }}
                                      />
                                    ))}
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {formatMessageDate(message.created)}
                            </Typography>
                          </Box>
                          
                          {!isFromUser && (
                            <Avatar 
                              sx={{ 
                                width: 32, 
                                height: 32, 
                                ml: 1,
                                mt: 'auto',
                                bgcolor: theme.palette.secondary.main
                              }}
                            >
                              <Storefront />
                            </Avatar>
                          )}
                        </Box>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Message Input */}
                <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                  <form onSubmit={handleSendMessage}>
                    {selectedFiles.length > 0 && (
                      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedFiles.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              position: 'relative',
                              width: 60,
                              height: 60,
                              borderRadius: 1,
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              component="img"
                              src={URL.createObjectURL(file)}
                              alt={`Selected ${index}`}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveFile(index)}
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                bgcolor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                p: '2px',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                      />
                      <IconButton
                        color="primary"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={sending}
                      >
                        <AttachFileIcon />
                      </IconButton>
                      
                      <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder="Type a message as seller..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        disabled={sending}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(e);
                          }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                      />
                      
                      <Button
                        variant="contained"
                        color="secondary"
                        endIcon={<SendIcon />}
                        disabled={sending || (!newMessage.trim() && !selectedFiles.length)}
                        type="submit"
                        sx={{ borderRadius: 2 }}
                      >
                        Send
                      </Button>
                    </Box>
                  </form>
                </Box>
              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '100%',
                p: 3,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)'
              }}>
                <MessageIcon sx={{ fontSize: 100, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Select a conversation
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  Choose a conversation from the list to view messages
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default SellerChat;